import type {
  CardCatalog,
  CardDefinition,
  CardId,
  Seat,
  WordOccurrence
} from "./model";
import { assertUnicodeScalarString, compareUtf8Bytes } from "./utf8";

const ASCII_UPPER = /[A-Z]/g;

export function normalizeMatcherText(value: string): string {
  assertUnicodeScalarString(value, "matcher input");
  return value
    .normalize("NFC")
    .replace(ASCII_UPPER, (character) => character.toLowerCase());
}

function isAsciiWordCodePoint(value: string | undefined): boolean {
  if (value === undefined) {
    return false;
  }
  return /^[a-z0-9]$/.test(value);
}

function findFormSpans(
  normalizedText: readonly string[],
  normalizedForm: readonly string[],
  mode: "SUBSTRING" | "WORD"
): { start: number; end: number }[] {
  if (normalizedForm.length === 0 || normalizedForm.length > normalizedText.length) {
    return [];
  }

  const spans: { start: number; end: number }[] = [];
  const lastStart = normalizedText.length - normalizedForm.length;

  for (let start = 0; start <= lastStart; start += 1) {
    let matches = true;
    for (let offset = 0; offset < normalizedForm.length; offset += 1) {
      if (normalizedText[start + offset] !== normalizedForm[offset]) {
        matches = false;
        break;
      }
    }

    if (!matches) {
      continue;
    }

    const end = start + normalizedForm.length;
    if (
      mode === "WORD" &&
      (isAsciiWordCodePoint(normalizedText[start - 1]) ||
        isAsciiWordCodePoint(normalizedText[end]))
    ) {
      continue;
    }

    spans.push({ start, end });
  }

  return spans;
}

function compareOccurrences(left: WordOccurrence, right: WordOccurrence): number {
  return (
    left.span.start - right.span.start ||
    left.span.end - right.span.end ||
    compareUtf8Bytes(left.cardId, right.cardId) ||
    left.formIndex - right.formIndex
  );
}

export function findWordOccurrences(
  text: string,
  eligibleCardsBySeat: Readonly<Record<string, readonly CardId[]>>,
  cards: CardCatalog
): { normalizedText: string; occurrences: WordOccurrence[] } {
  const normalizedText = normalizeMatcherText(text);
  const textCodePoints = Array.from(normalizedText);
  const candidates: WordOccurrence[] = [];

  const seatEntries = Object.entries(eligibleCardsBySeat).sort(
    ([left], [right]) => Number(left) - Number(right)
  );

  for (const [seatKey, cardIds] of seatEntries) {
    const owner = Number(seatKey) as Seat;
    for (const cardId of [...cardIds].sort(compareUtf8Bytes)) {
      const card = cards[cardId];
      if (card === undefined) {
        throw new Error(`Unknown eligible card: ${cardId}`);
      }
      assertUnicodeScalarString(card.cardId, `cardId ${card.cardId}`);

      for (const [formIndex, form] of card.matchForms.entries()) {
        if (form.mode !== "SUBSTRING" && form.mode !== "WORD") {
          throw new Error(`UNKNOWN_MATCH_MODE: ${String(form.mode)}`);
        }
        const normalizedForm = normalizeMatcherText(form.text);
        if (normalizedForm.length === 0) {
          throw new Error(`EMPTY_MATCH_FORM: ${card.cardId}#${formIndex}`);
        }
        const formCodePoints = Array.from(normalizedForm);
        for (const span of findFormSpans(
          textCodePoints,
          formCodePoints,
          form.mode
        )) {
          candidates.push({
            cardId,
            owner,
            formIndex,
            matchFormText: form.text,
            normalizedMatchText: normalizedForm,
            mode: form.mode,
            span
          });
        }
      }
    }
  }

  candidates.sort(compareOccurrences);

  return { normalizedText, occurrences: candidates };
}

export function findAccusedCard(
  hand: readonly CardId[],
  guess: string,
  cards: CardCatalog
): CardDefinition | undefined {
  const normalizedGuess = normalizeMatcherText(guess);
  return hand
    .map((cardId) => cards[cardId])
    .filter((card): card is CardDefinition => card !== undefined)
    .find(
      (card) => normalizeMatcherText(card.displayText) === normalizedGuess
    );
}
